/**
 * parseClipboardLines — convert pasted text into task titles.
 *
 * Handles:
 * - Plain text (one task per line)
 * - Markdown task lists (`- [ ] Title`, `- [x] Title`)
 * - Markdown unordered lists (`- Title`, `* Title`)
 * - Numbered lists (`1. Title`, `1) Title`)
 * - Filters empty lines and whitespace-only lines
 *
 * Returns cleaned title strings, never empty strings.
 */

// ---------------------------------------------------------------------------
// Markdown patterns
// ---------------------------------------------------------------------------

/** Matches `- [ ] Title` or `- [x] Title` or `* [ ] Title` etc. */
const TASK_LIST_RE = /^[-*+]\s*\[[ xX]\]\s+(.+)/

/** Matches `- Title` or `* Title` or `+ Title` */
const UNORDERED_LIST_RE = /^[-*+]\s+(.+)/

/** Matches `1. Title` or `1) Title` */
const ORDERED_LIST_RE = /^\d+[.)]\s+(.+)/

/** Matches `# Heading` — skip headings (not tasks) */
const HEADING_RE = /^#{1,6}\s+/

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

export function parseClipboardLines(text: string): string[] {
  const lines = text.split(/\r?\n/)
  const titles: string[] = []

  for (const raw of lines) {
    const line = raw.trim()
    if (!line) continue

    // Skip headings
    if (HEADING_RE.test(line)) continue

    // Try task list first
    const taskMatch = TASK_LIST_RE.exec(line)
    if (taskMatch) {
      const title = taskMatch[1]!.trim()
      if (title) titles.push(title)
      continue
    }

    // Try unordered list
    const ulMatch = UNORDERED_LIST_RE.exec(line)
    if (ulMatch) {
      const title = ulMatch[1]!.trim()
      if (title) titles.push(title)
      continue
    }

    // Try ordered list
    const olMatch = ORDERED_LIST_RE.exec(line)
    if (olMatch) {
      const title = olMatch[1]!.trim()
      if (title) titles.push(title)
      continue
    }

    // Plain text — use the whole line as a title
    titles.push(line)
  }

  return titles
}
